import React, { useState, useEffect } from "react";
import axios from "axios";
import { FiPlus } from "react-icons/fi";

import { useFormatValue } from "../../../hooks/useFormatValue";
import { translate } from "../../../hooks/translate";
import { useAuth } from "../../../services/useAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import { withAuth } from "../../../components/HOC/withAuth";
import { useColumnVisibility } from "../../../hooks/useColumnVisibility";

import AppPage from "../../../components/CustomUI/AppPage";
import ListViewOptionsPanel from "../shared/ListViewOptionsPanel";
import Container from "../../../components/CustomUI/Container";
import ContainerItem from "../../../components/CustomUI/Container/ContainerItem";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import Button from "../../../components/BaseUI/Button";
import Lozenge from "../../../components/BaseUI/Lozenge";
import { useTitle } from "../../../hooks/useTitle";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const SerialNumbers = () => {
  const [serialNumbers, setSerialNumbers] = useState([]);
  const [toasts, setToasts] = useState([]);
  const [loading, setLoading] = useState(false);

  const format = useFormatValue;
  const { getToken, getUser } = useAuth();
  const currentUser = getUser();
  const canWrite =
    !!currentUser &&
    hasPageWriteAccess(
      currentUser.role,
      "/serial-numbers",
      currentUser.pages || [],
      !!currentUser.fullAccess,
    );
  useTitle("Rovion MES - Seri Numaraları");
  const headers = [
    { name: translate("UI.SERIAL_NUMBER"), dataType: "text", id: 0 },
    {
      name: translate("UI.STATUS"),
      dataType: "text",
      id: 1,
      customSorter: (a, b) => {
        const statusA = a?.status || "";
        const statusB = b?.status || "";
        const statusOrder = {
          assigned: 1,
          available: 2,
        };

        return (statusOrder[statusA] || 99) - (statusOrder[statusB] || 99);
      },
    },
    { name: translate("UI.CREATED"), dataType: "date", id: 2 },
  ];

  // Use column visibility hook
  const { visibleColumns, toggleColumnVisibility, resetToDefault } =
    useColumnVisibility("serial-number-list", headers);

  const breadcrumbs = [
    { label: "Rovion", url: "" },
    { label: translate("UI.SERIAL_NUMBERS") },
  ];

  const fetchSerialNumbers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/serial-numbers`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      setSerialNumbers(response.data);
    } catch (error) {
      console.error("Seri numarası getirme hatası:", error);
      setToasts([
        {
          description:
            error.response?.data?.message ||
            translate("UI.SERIAL_NUMBER_FETCH_ERROR"),
          id: Date.now(),
          title: "Hata!",
          appearance: "danger",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchSerialNumbers();
  }, []);

  const generateSerialNumber = async () => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${API_URL}/api/serial-numbers/generate`,
        {},
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        },
      );

      if (response.data.success) {
        setToasts([
          {
            description: translate("UI.SERIAL_NUMBER_GENERATE_SUCCESS"),
            id: Date.now(),
            title: "Başarılı!",
            appearance: "success",
          },
        ]);
        fetchSerialNumbers();
      }
    } catch (error) {
      console.error("Seri numarası oluşturma hatası:", error);
      setToasts([
        {
          description:
            error.response?.data?.message ||
            translate("UI.SERIAL_NUMBER_GENERATE_ERROR"),
          id: Date.now(),
          title: "Hata!",
          appearance: "danger",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const removeToast = (index) => {
    const values = toasts;
    values.splice(index, 1);
    setToasts([...values]);
  };

  const getStatusAppearance = (status) => {
    switch (status) {
      case "available":
        return "success-subtle";
      case "assigned":
        return "warning-subtle";
      default:
        return "neutral-subtle";
    }
  };

  return (
    <AppPage
      breadcrumbs={breadcrumbs}
    >
      <Container>
        <ContainerItem sm={4} md={8} xl={12}>
          <div className="page-header-card p-3 d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="mb-0">{translate("UI.SERIAL_NUMBERS")}</h2>
            </div>
            {canWrite && (
              <div className="d-flex gap-2">
                <Button
                  appearance="primary-animated"
                  onClick={generateSerialNumber}
                  icon={<FiPlus />}
                  loading={loading}
                >
                  {translate("UI.GENERATE_SERIAL_NUMBER")}
                </Button>
              </div>
            )}
          </div>
        </ContainerItem>
        <ContainerItem sm={4} md={8} xl={12}>
          <div className="card card-body p-3 d-flex justify-content-between align-items-center mb-4">
            <Table
              columns={headers}
              visibleColumns={visibleColumns}
              tableId="serial-number-list"
              loading={loading}
              viewOptions={
                <ListViewOptionsPanel
                  columns={headers}
                  visibleColumns={visibleColumns}
                  onToggleColumn={toggleColumnVisibility}
                  onResetColumns={resetToDefault}
                  showApply={false}
                  showReset={false}
                />
              }
            >
              {serialNumbers.map((serial, index) => (
                <Tr
                  key={index}
                  id={serial?.serialNumber || index}
                  data={serial}
                >
                  {visibleColumns.includes(0) && <Td>{serial.serialNumber}</Td>}
                  {visibleColumns.includes(1) && (
                    <Td>
                      <Lozenge appearance={getStatusAppearance(serial.status)}>
                        {translate(`UI.${serial.status?.toUpperCase()}`)}
                      </Lozenge>
                    </Td>
                  )}
                  {visibleColumns.includes(2) && (
                    <Td>{format(serial.createdAt, "date")}</Td>
                  )}
                </Tr>
              ))}
            </Table>
          </div>
        </ContainerItem>
      </Container>

      <ToastContainer>
        {toasts?.map((toast, index) => (
          <Toast
            {...toast}
            key={index}
            // actions={[{ content: "Kapat", onClick: () => removeToast(index) }]}
            onDismissed={() => removeToast(index)}
            onClose={() => removeToast(index)}
          />
        ))}
      </ToastContainer>
    </AppPage>
  );
};

export default withAuth(SerialNumbers, ["admin", "employee"]);
